import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Mail, Eye, EyeOff, FolderKanban, Users, BarChart3, ArrowRight, ArrowLeft, Loader2, CheckCircle, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const Auth = ({ onLogin }) => {
  const [searchParams] = useSearchParams();
  const initialView = searchParams.get('view') === 'register' ? 'register' : 'login';
  const [showPassword, setShowPassword] = useState(false);
  const [view, setView] = useState(initialView); // 'login', 'register', 'forgot'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (!formData.email || !formData.password) {
      setError('L\'email et le mot de passe sont requis');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, password: formData.password })
      });
      const data = await response.json();

      if (data.error) {
        setError(data.error);
        setIsLoading(false);
        return;
      }

      localStorage.setItem('currentUser', JSON.stringify(data.user));
      onLogin(data.user);
    } catch (err) {
      setError('Impossible de contacter le serveur. Vérifiez votre connexion.');
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (!formData.email || !formData.password) {
      setError('L\'email et le mot de passe sont requis');
      setIsLoading(false);
      return;
    }
    if (formData.password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: formData.name, email: formData.email, password: formData.password })
      });
      const data = await response.json();

      if (data.error) {
        setError(data.error);
        setIsLoading(false);
        return;
      }

      localStorage.setItem('currentUser', JSON.stringify(data.user));
      onLogin(data.user);
    } catch (err) {
      setError('Impossible de contacter le serveur. Vérifiez votre connexion.');
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    if (!formData.email) {
      setError('Veuillez entrer votre email');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, origin_url: window.location.origin })
      });
      const data = await response.json();

      setSuccess(data.message || 'Si un compte existe avec cet email, un lien de réinitialisation a été envoyé.');
      setIsLoading(false);
    } catch (err) {
      setError('Impossible de contacter le serveur. Vérifiez votre connexion.');
      setIsLoading(false);
    }
  };

  const features = [
    {
      icon: FolderKanban,
      title: 'Gestion de Projets',
      description: 'Suivez vos projets web de A à Z'
    },
    {
      icon: Users,
      title: 'Équipe & Collaboration',
      description: 'Gérez votre équipe efficacement'
    },
    {
      icon: BarChart3,
      title: 'Performance',
      description: 'Analysez vos indicateurs clés'
    },
  ];

  const switchView = (newView) => {
    setView(newView);
    setError('');
    setSuccess('');
  };

  return (
    <div className="flex min-h-screen" data-testid="auth-page">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-2/5 bg-primary relative overflow-hidden flex-col">
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-64 h-64 border border-white/10 rounded-full" />
          <div className="absolute bottom-40 left-10 w-48 h-48 border border-white/10 rounded-full" />
          <div className="absolute top-1/2 left-1/3 w-32 h-32 border border-white/10 rounded-full" />
        </div>
        
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
              <span className="text-white font-bold text-xl">C</span>
            </div>
            <span className="text-xl font-semibold">CREA FLOW</span>
          </div>

          {/* Main Content */}
          <div className="space-y-6">
            <h1 className="text-4xl lg:text-5xl font-bold leading-tight">
              Pilotez vos projets web comme un pro
            </h1>
            <p className="text-lg text-white/80">
              Outil de gestion de projets moderne et intuitif pour les professionnels organisés.
            </p>
          </div>

          {/* Features */}
          <div className="grid grid-cols-1 gap-4">
            {features.map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + idx * 0.1 }}
                className="flex items-start gap-4 p-4 rounded-lg bg-white/10 backdrop-blur-sm"
              >
                <feature.icon className="w-6 h-6 text-white/80 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-white">{feature.title}</h3>
                  <p className="text-sm text-white/70">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Forms */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-xl">C</span>
            </div>
            <span className="text-xl font-semibold">CREA FLOW</span>
          </div>

          <div className="bg-card border border-border rounded-xl p-8 shadow-sm">
            {view === 'login' ? (
              <>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Lock className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold">Connexion</h2>
                    <p className="text-sm text-muted-foreground">Accès réservé aux collaborateurs</p>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5 mt-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="email"
                        placeholder="vous@exemple.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="pl-10"
                        data-testid="auth-email-input"
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium">Mot de passe</label>
                      <button
                        type="button"
                        onClick={() => switchView('forgot')}
                        className="text-xs text-primary hover:underline"
                      >
                        Mot de passe oublié ?
                      </button>
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Votre mot de passe"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="pl-10 pr-10"
                        data-testid="auth-password-input"
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        data-testid="toggle-password"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {error && (
                    <div className="bg-destructive/10 border border-destructive/20 text-destructive text-sm p-3 rounded-md" data-testid="auth-error">
                      {error}
                    </div>
                  )}

                  <Button 
                    type="submit" 
                    className="w-full" 
                    data-testid="auth-submit-button"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Connexion...
                      </>
                    ) : (
                      <>
                        Se connecter
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>

                <div className="mt-5 text-center space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Pour obtenir un accès, contactez votre administrateur.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Pas encore de compte ?{' '}
                    <button
                      type="button"
                      onClick={() => switchView('register')}
                      className="text-primary font-medium hover:underline"
                      data-testid="switch-to-register"
                    >
                      Créer un compte gratuit
                    </button>
                  </p>
                </div>
              </>
            ) : view === 'register' ? (
              <>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <User className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold">Créer un compte</h2>
                    <p className="text-sm text-muted-foreground">Testez CREA FLOW gratuitement</p>
                  </div>
                </div>

                <div className="mt-4 mb-5 p-3 rounded-lg bg-blue-50 border border-blue-100">
                  <p className="text-sm text-blue-700">
                    Le compte gratuit vous permet de créer 1 projet et de tester l'outil. Passez au plan Pro à tout moment pour tout débloquer.
                  </p>
                </div>

                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Nom</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder="Votre nom"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="pl-10"
                        data-testid="register-name-input"
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="email"
                        placeholder="vous@exemple.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="pl-10"
                        data-testid="register-email-input"
                        disabled={isLoading}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Mot de passe</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Minimum 6 caractères"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="pl-10 pr-10"
                        data-testid="register-password-input"
                        disabled={isLoading}
                        required
                        minLength={6}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {error && (
                    <div className="bg-destructive/10 border border-destructive/20 text-destructive text-sm p-3 rounded-md" data-testid="register-error">
                      {error}
                    </div>
                  )}

                  <Button 
                    type="submit" 
                    className="w-full" 
                    data-testid="register-submit-button"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Création du compte...
                      </>
                    ) : (
                      <>
                        Créer mon compte gratuit
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-sm text-muted-foreground">
                    Déjà un compte ?{' '}
                    <button
                      type="button"
                      onClick={() => switchView('login')}
                      className="text-primary font-medium hover:underline"
                      data-testid="switch-to-login"
                    >
                      Se connecter
                    </button>
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Mail className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold">Mot de passe oublié</h2>
                    <p className="text-sm text-muted-foreground">Recevez un lien de réinitialisation</p>
                  </div>
                </div>

                <form onSubmit={handleForgotPassword} className="space-y-5 mt-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="email"
                        placeholder="vous@exemple.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="pl-10"
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  {error && (
                    <div className="bg-destructive/10 border border-destructive/20 text-destructive text-sm p-3 rounded-md">
                      {error}
                    </div>
                  )}

                  {success && (
                    <div className="bg-green-100 border border-green-200 text-green-700 text-sm p-3 rounded-md flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>{success}</span>
                    </div>
                  )}

                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={isLoading || success}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Envoi en cours...
                      </>
                    ) : (
                      'Envoyer le lien'
                    )}
                  </Button>
                </form>

                <button
                  type="button"
                  onClick={() => switchView('login')}
                  className="flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground mt-6 w-full"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Retour à la connexion
                </button>
              </>
            )}
          </div>

          <div className="mt-6 text-center text-xs text-muted-foreground">
            <p>&copy; {new Date().getFullYear()} CREA FLOW - CREA DIGI. Tous droits réservés.</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Auth;
